import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Modal, Input, Select, Loading, EmptyState, Toggle, SectionLabel } from '../components/ui';

const SCREEN_GROUPS = [
  {
    group: 'Masters',
    items: [
      { key: 'masters_home', label: 'Masters home' },
      { key: 'masters_divisions', label: 'Division master' },
      { key: 'masters_factories', label: 'Factory master' },
      { key: 'masters_shiftlines', label: 'Shift-line master' },
      { key: 'masters_operations', label: 'Operation master' },
      { key: 'costing', label: 'Costing (master & sheets)' },
      { key: 'masters_fundgroups', label: 'Fund. operation master' },
      { key: 'masters_machines', label: 'Machine master' },
      { key: 'masters_employees', label: 'Employee master' },
      { key: 'masters_downtime_reasons', label: 'Downtime reason master' },
    ]
  },
  { group: 'Planning', items: [
      { key: 'ob', label: 'Operation bulletin' },
      { key: 'line_design', label: 'Line design' },
      { key: 'monthly_summary', label: 'Monthly summary' },
    ]
  },
  { group: 'Operations', items: [
      { key: 'deployment', label: 'Deployment' },
      { key: 'daily_balancing', label: 'Daily balancing' },
      { key: 'hourly_reporting', label: 'Hourly reporting' },
    ]
  },
  { group: 'Reports', items: [
      { key: 'skill_matrix', label: 'Skill matrix' },
      { key: 'skill_journey', label: 'Skill journey' },
    ]
  },
  { group: 'Dashboards', items: [
      { key: 'dashboard_supervisor', label: 'Supervisor dashboard' },
      { key: 'dashboard_plant', label: 'Plant head dashboard' },
      { key: 'dashboard_downtime', label: 'Downtime dashboard' },
      { key: 'plant_optimiser', label: 'Plant optimiser' },
    ]
  },
  // NOTE: "User access" and "Role master" are intentionally NOT listed here.
  // Access to those two screens is hardcoded to true super admins only —
  // it can never be granted through a permission toggle.
];

export default function UserAccess() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null); // null = landing view

  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', password: '', role_id: '', factory_id: '' });
  const [error, setError] = useState('');

  const [perms, setPerms] = useState({});
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsSaved, setPermsSaved] = useState(false);

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'User access'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, f] = await Promise.all([
        api.get('/users'), api.get('/roles'), api.get('/factories')
      ]);
      setUsers(u.data); setRoles(r.data); setFactories(f.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchPerms = useCallback(async (userId) => {
    setPermsLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}/permissions`);
      const map = {};
      data.forEach(p => { map[p.screen_key] = p.can_access; });
      setPerms(map);
    } catch (e) { console.error(e); }
    setPermsLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (selectedUser) fetchPerms(selectedUser.id); }, [selectedUser, fetchPerms]);

  const openAdd = () => {
    setEditingUser(null);
    setForm({ full_name: '', email: '', password: '', role_id: '', factory_id: '' });
    setError(''); setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ full_name: u.full_name, email: u.email, password: '', role_id: u.role_id || '', factory_id: u.factory_id || '' });
    setError(''); setModalOpen(true);
  };

  const handleSaveUser = async () => {
    if (!form.full_name.trim()) { setError('Name is required'); return; }
    if (!editingUser && !form.email.trim()) { setError('Email is required'); return; }
    if (!editingUser && !form.role_id) { setError('Role is required'); return; }
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          full_name: form.full_name,
          role: roles.find(r => r.id === Number(form.role_id))?.name || editingUser.role,
          factory_id: form.factory_id || null,
          is_active: editingUser.is_active,
          email: form.email !== editingUser.email ? form.email : undefined,
          password: form.password ? form.password : undefined,
        });
      } else {
        await api.post('/users', {
          full_name: form.full_name,
          email: form.email,
          password: form.password,
          role_id: Number(form.role_id),
          factory_id: form.factory_id || null,
        });
      }
      setModalOpen(false); fetchAll();
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.full_name}" (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Error deleting'); }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { full_name: u.full_name, role: u.role, factory_id: u.factory_id, is_active: !u.is_active });
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const togglePerm = (key) => {
    setPerms(p => ({ ...p, [key]: !p[key] }));
    setPermsSaved(false);
  };

  const handleSavePerms = async () => {
    const permissions = Object.keys(perms).map(screen_key => ({ screen_key, can_access: !!perms[screen_key] }));
    try {
      await api.put(`/users/${selectedUser.id}/permissions`, { permissions });
      setPermsSaved(true);
    } catch (e) { alert(e.response?.data?.error || 'Error saving permissions'); }
  };

  const roleOptions = roles.map(r => ({ value: r.id, label: r.name }));
  const factoryOptions = factories.map(f => ({ value: f.id, label: f.name }));

  // ── Landing: user list ────────────────────────────────────────────────────
  if (!selectedUser) {
    return (
      <div style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>
              User access <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{users.length} users</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Create users, assign roles, override individual screen access</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Btn variant="primary" size="sm" onClick={openAdd}>+ Add user</Btn>
          </div>
        </div>

        {loading ? <Loading /> : users.length === 0 ? (
          <EmptyState message="No users yet" action={<Btn variant="primary" onClick={openAdd}>+ Add user</Btn>} />
        ) : (
          <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
            <table className="lc-table">
              <thead>
                <tr><th>#</th><th>Name</th><th>Email</th><th>Role</th><th>Factory</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id}>
                    <td style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{u.full_name}</td>
                    <td style={{ fontSize: 11.5 }}>{u.email}</td>
                    <td><Badge type="blue">{u.role}</Badge></td>
                    <td style={{ fontSize: 11.5 }}>{u.factory_name || '—'}</td>
                    <td><Badge type={u.is_active ? 'green' : 'red'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></td>
                    <td style={{ display: 'flex', gap: 10 }}>
                      <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => setSelectedUser(u)}>Permissions →</a>
                      <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(u)}>Edit</a>
                      <a style={{ fontSize: 11, color: u.is_active ? 'var(--red)' : 'var(--grn)', cursor: 'pointer' }} onClick={() => handleToggleActive(u)}>
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </a>
                      <a style={{ fontSize: 11, color: 'var(--red)', cursor: 'pointer' }} onClick={() => handleDeleteUser(u)}>Delete</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingUser ? 'Edit user' : 'Add user'} width={380}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Full name</div>
              <Input value={form.full_name} onChange={v => setForm(p => ({ ...p, full_name: v }))} style={{ width: '100%' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Email (used as login)</div>
              <Input value={form.email} onChange={v => setForm(p => ({ ...p, email: v }))} placeholder="name@company.com" style={{ width: '100%' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                {editingUser ? 'Reset password (optional)' : 'Password'}
              </div>
              <Input type="password" value={form.password} onChange={v => setForm(p => ({ ...p, password: v }))}
                placeholder={editingUser ? 'Leave blank to keep current password' : 'Leave blank for default'} style={{ width: '100%' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Role</div>
              <Select value={form.role_id} onChange={v => setForm(p => ({ ...p, role_id: v }))} options={roleOptions} placeholder="Select role" style={{ width: '100%' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Factory (optional — leave blank for all units)</div>
              <Select value={form.factory_id} onChange={v => setForm(p => ({ ...p, factory_id: v }))} options={factoryOptions} placeholder="All units" style={{ width: '100%' }} />
            </div>
            {error && <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={handleSaveUser}>Save</Btn>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  // ── Detail: permission overrides for selected user ───────────────────────
  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <a onClick={() => { setSelectedUser(null); setPermsSaved(false); }} style={{ fontSize: 12, color: 'var(--acc)', cursor: 'pointer' }}>← All users</a>
        <span style={{ color: 'var(--border-md)' }}>/</span>
        <div>
          <span style={{ fontSize: 15, fontWeight: 500 }}>{selectedUser.full_name}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>Screen access ({selectedUser.role})</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {permsSaved && <span style={{ fontSize: 11, color: 'var(--grn)' }}>Saved ✓</span>}
          <Btn variant="primary" size="sm" onClick={handleSavePerms}>Save permissions</Btn>
        </div>
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: 8, marginBottom: 14 }}>
        These were pre-filled from the "{selectedUser.role}" role when the user was created. Overriding here only affects this user.
      </div>

      {permsLoading ? <Loading /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14 }}>
          {SCREEN_GROUPS.map(section => (
            <div key={section.group} style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, padding: 14, background: 'var(--bg-primary)' }}>
              <SectionLabel>{section.group}</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {section.items.map(item => (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 12.5 }}>{item.label}</div>
                    <Toggle value={!!perms[item.key]} onChange={() => togglePerm(item.key)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}